if (!customElements.get('product-bundle-offer')) {
	customElements.define('product-bundle-offer', class ProductBundleOffer extends HTMLElement {
		connectedCallback() {
			this.productSelect = this.querySelector('[data-bundle-product]');
			this.submitButton = this.querySelector('[data-bundle-submit]');
			this.tooltip = this.querySelector('[data-bundle-tooltip]');
			this.currentVariantId = this.dataset.currentVariantId;
			this.currentPrice = Number(this.dataset.currentPrice);
			this.currentSelectionReady = this.dataset.currentSelectionReady === 'true';
			this.discount = Number(this.dataset.discount) || 0;
			this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

			this.productSelect?.addEventListener('change', () => this.onProductChange());
			this.querySelectorAll('[data-bundle-variant]').forEach((select) => {
				select.addEventListener('change', () => this.update());
			});
			this.submitButton?.addEventListener('click', () => this.addBoth());
			document.addEventListener('variant:change', this.onMainVariantChange);
			document.addEventListener('pointerdown', this.onDocumentPointerDown);
			document.addEventListener('keydown', this.onDocumentKeyDown);
			if (this.tooltip && this.submitButton) {
				ProductBundleOffer.tooltipIndex = (ProductBundleOffer.tooltipIndex || 0) + 1;
				this.tooltip.id = `BundleTooltip-${this.dataset.sectionId}-${ProductBundleOffer.tooltipIndex}`;
				this.submitButton.setAttribute('aria-describedby', this.tooltip.id);
			}
			this.update();
		}

		disconnectedCallback() {
			document.removeEventListener('variant:change', this.onMainVariantChange);
			document.removeEventListener('pointerdown', this.onDocumentPointerDown);
			document.removeEventListener('keydown', this.onDocumentKeyDown);
			window.clearTimeout(this.tooltipTimer);
		}

		onDocumentPointerDown = (event) => {
			if (!this.contains(event.target)) this.hideTooltip();
		};

		onDocumentKeyDown = (event) => {
			if (event.key === 'Escape') this.hideTooltip();
		};

		showTooltip() {
			if (!this.tooltip) return;
			this.tooltip.hidden = false;
			window.clearTimeout(this.tooltipTimer);
			this.tooltipTimer = window.setTimeout(() => this.hideTooltip(), 5000);
		}

		hideTooltip() {
			if (this.tooltip) this.tooltip.hidden = true;
			window.clearTimeout(this.tooltipTimer);
		}

		onMainVariantChange = (event) => {
			if (event.detail.sectionId !== this.dataset.sectionId || !event.detail.variant) return;
			this.currentVariantId = String(event.detail.variant.id);
			this.currentPrice = Number(event.detail.variant.price);
			this.currentSelectionReady = true;
			const image = event.detail.variant.featured_image?.src || event.detail.variant.featured_media?.preview_image?.src;
			const currentImage = this.querySelector('[data-bundle-current-image]');
			if (image && currentImage) currentImage.src = image;
			this.update();
		};

		get activeVariantSelect() {
			const id = this.productSelect?.selectedOptions[0]?.dataset.variantsId;
			return id
				? Array.from(this.querySelectorAll('[data-bundle-variants]')).find((field) => field.id === id)?.querySelector('[data-bundle-variant]')
				: null;
		}

		onProductChange() {
			this.querySelectorAll('[data-bundle-variants]').forEach((field) => field.classList.add('hidden'));
			const id = this.productSelect.selectedOptions[0]?.dataset.variantsId;
			const activeField = Array.from(this.querySelectorAll('[data-bundle-variants]')).find((field) => field.id === id);
			if (activeField) {
				activeField.classList.remove('hidden');
				activeField.querySelector('[data-bundle-variant]').selectedIndex = 0;
			}
			this.update();
		}

		update() {
			const select = this.activeVariantSelect;
			const selectionComplete = Boolean(this.currentSelectionReady && this.productSelect?.value && select?.value);
			this.selectionComplete = selectionComplete;
			this.submitButton?.setAttribute('aria-disabled', String(!selectionComplete));
			if (selectionComplete) this.hideTooltip();
			const option = select?.value
				? select.selectedOptions[0]
				: Array.from(select?.options || []).find((item) => item.value && !item.disabled);
			if (!option) return;
			const regularPrice = this.currentPrice + Number(option.dataset.price);
			const discountedPrice = Math.round(regularPrice * (100 - this.discount) / 100);
			this.querySelector('[data-bundle-regular-price]').textContent = formatMoney(regularPrice);
			this.querySelector('[data-bundle-discounted-price]').textContent = formatMoney(discountedPrice);
			this.querySelector('[data-bundle-saving]').textContent = `-${formatMoney(regularPrice - discountedPrice)}`;
			const image = this.querySelector('[data-bundle-second-image]');
			if (image && option.dataset.image) image.src = option.dataset.image;
		}

		async addBoth() {
			if (this.isSubmitting) return;
			const secondVariantId = this.activeVariantSelect?.value;
			const message = this.querySelector('[data-bundle-message]');
			if (!this.selectionComplete || !this.currentVariantId || !secondVariantId) {
				this.showTooltip();
				return;
			}

			this.hideTooltip();
			this.isSubmitting = true;
			this.submitButton.setAttribute('aria-disabled', 'true');
			this.submitButton.classList.add('loading');
			this.submitButton.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
			message.hidden = true;

			const body = {
				items: [
					{ id: Number(this.currentVariantId), quantity: 1 },
					{ id: Number(secondVariantId), quantity: 1 }
				]
			};
			if (this.cart) {
				body.sections = this.cart.getSectionsToRender().map((section) => section.id);
				body.sections_url = window.location.pathname;
				this.cart.setActiveElement(document.activeElement);
			}

			try {
				const response = await fetch(routes.cart_add_url, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
					body: JSON.stringify(body)
				});
				const result = await response.json();
				if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this bundle.');
				// The cart drawer notification normally receives one variant id. A bundle
				// adds two line items, so pass both ids to keep both newly added items visible.
				result.id = Number(this.currentVariantId);
				result.ids = [Number(this.currentVariantId), Number(secondVariantId)];
				document.dispatchEvent(new CustomEvent('cart:change', { detail: result, bubbles: true }));
				if (this.cart) this.cart.renderContents(result);
				else window.location = window.routes.cart_url;
			} catch (error) {
				message.textContent = error.message;
				message.hidden = false;
			} finally {
				this.isSubmitting = false;
				this.submitButton.classList.remove('loading');
				this.submitButton.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
				this.update();
			}
		}
	});
}
